import { useEffect, useMemo, useState } from "react";
import { tx } from "modules/language/translate";
import login from "modules/api/auth-service/login";
import { OAuthFormFragment } from "./OAuthProviders";
import { Cooldown } from "components/security/Cooldown";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  EmailPasswordFormFragment,
  EmailPasswordFormValues,
  formFieldsInvalid,
} from "./EmailPasswordFormFragment";
import { Card, Text, Form, Button, FormRow } from "frontend-components";
import { OtpForm } from "./OtpForm";
import { FetchError } from "modules/api/utils/fetch";

import "./AuthForms.scss";

export type LoginType = "oauth" | "dashboard";

/**
 * Form to enter email and password to login.
 * Handles OTP and 3rd party OAuth login as well.
 * onError is isolated to this form and will always display
 * a toast when failing to sign in.
 * Component shared by login and OAuth2 authz pages.
 * @param param0
 * @returns
 */
export const LoginForm = ({
  submitText = tx("auth.actions.sign_in"),
  loginType = "dashboard",
  includeOAuth = true,
  verificationToken,
  email,
  newEmail,
  onSuccess,
  onFailure,
  onCooldown,
  onCooldownOff,
}: {
  submitText?: string;
  loginType?: LoginType;
  includeOAuth?: boolean;
  verificationToken?: string;
  email?: string;
  newEmail?: string;
  onSuccess: (form: EmailPasswordFormValues) => void;
  onFailure?: (error: {
    title: string;
    description: string;
    code: string;
  }) => void;
  onCooldown?: () => void;
  onCooldownOff?: () => void;
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loginError, setLoginError] = useState<string | null>(null);
  const [form, setForm] = useState<EmailPasswordFormValues>({
    email: email || "",
    password: "",
    otp: "",
  });
  const [showOtp, setShowOtp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginCooldown, setLoginCooldown] = useState(false);

  useEffect(() => {
    if (searchParams.get("invalid-session") === "true") {
      setLoginError(tx("errors.invalid_session_message"));
    } else if (searchParams.get("oauth-failed") === "true") {
      const authProviderError =
        searchParams.get("auth-provider-error") === "true";
      const authProviderErrorMessage =
        searchParams.get("error-description")?.replace(/%20/g, " ") ||
        tx("errors.try_again");
      const emailAlreadyInUse =
        searchParams.get("email-already-in-use") === "true";
      const message = emailAlreadyInUse
        ? tx("auth.errors.email_already_in_use")
        : authProviderError
        ? authProviderErrorMessage
        : tx("errors.try_again");
      setLoginError(message);
    }
  }, [searchParams]);

  const onError = (title: string, description: string, code: string) => {
    if (onFailure) {
      onFailure({ title, description, code });
    } else {
      setLoginError(description);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formFieldsInvalid(form)) {
      onError(
        tx("auth.errors.sign_in_fail"),
        "Form fields are invalid",
        "invalid_form"
      );
      return;
    }
    setLoading(true);
    await doLogin();
  };

  const doLogin = async () => {
    let loginInput: {
      email: string;
      password: string;
      otp?: string;
      type?: LoginType;
      verification_token?: string;
      new_email?: string;
    } = {
      email: form.email,
      password: form.password,
      type: loginType,
    };
    if (verificationToken) {
      loginInput.verification_token = verificationToken;
    }

    if (newEmail) {
      loginInput.new_email = newEmail;
    }

    if (showOtp) {
      loginInput.otp = form.otp;
    }

    try {
      await login(loginInput);
      onSuccess(form);
    } catch (e: any) {
      handleError(e);
    } finally {
      setLoading(false);
    }
  };

  const handleError = (error: any) => {
    let code: string | undefined;
    let status: number | undefined;
    let message: string | undefined;

    if (error instanceof FetchError) {
      code = error.code;
      status = error.status;
      message = error.message;
    } else {
      code = error.response?.data?.code;
      status = error?.response?.status;
      message = error.response?.data?.message;
    }

    switch (code) {
      case "invalid_credentials":
        onError(
          tx("auth.errors.sign_in_fail"),
          tx("auth.errors.invalid_credentials"),
          code
        );
        break;
      case "cooldown":
        onCooldown && onCooldown();
        if (onFailure) {
          onFailure({
            title: "Cooldown",
            description: "Cooldown",
            code: "cooldown",
          });
        } else {
          setLoginCooldown(true);
        }
        break;
      case "otp_required":
        setShowOtp(true);
        break;
      case "account_downgraded":
        onError(
          tx("auth.errors.sign_in_fail"),
          message ?? tx("errors.try_again"),
          code
        );
        break;
      case "session_limit_reached":
        onError(
          tx("auth.errors.sign_in_fail"),
          message ?? tx("errors.try_again"),
          code
        );
        break;
      case "missing_token":
        navigate(`/accounts/verify?email=${encodeURIComponent(form.email)}`);
        break;
      case "expired_token":
        onError(
          tx("auth.errors.expired_verification_link"),
          tx("auth.errors.invalid_verification_link"),
          code
        );
        break;
      case "invalid_token":
      case "too_many_token_resets":
        onError(
          tx("auth.errors.verification_fail"),
          tx("auth.errors.invalid_verification_link"),
          code
        );
        break;
      case "invalid_email":
        onError(
          tx("auth.errors.verification_fail"),
          tx("auth.errors.invalid_new_email", [newEmail ?? ""]),
          code
        );
        break;
      case "already_verified":
        onError(
          tx("auth.errors.verification_fail"),
          newEmail
            ? tx("auth.errors.already_verified_pending_email")
            : tx("auth.errors.already_verified"),
          code
        );
        break;
      case "no_email_change":
        onError(
          tx("auth.errors.verification_fail"),
          tx("auth.errors.no_pending_email"),
          code
        );
        break;
      case "account_suspended":
        onError(
          tx("auth.errors.sign_in_fail"),
          tx("auth.errors.account_suspended"),
          code
        );
        break;
      default:
        if (status === 401) {
          onError(
            tx("auth.errors.sign_in_fail"),
            tx("auth.errors.invalid_credentials"),
            code ?? "unknown_error"
          );
        } else {
          onError(
            tx("auth.errors.sign_in_fail"),
            message ?? tx("errors.try_again"),
            code ?? "unknown_error"
          );
        }
    }
  };

  const ErrorCard = useMemo(() => {
    if (!loginError) {
      return null;
    }
    return (
      <Card
        theme="error"
        width="full"
        height="min"
        padding="sm"
        rounded={false}
        shadow="none"
      >
        <Text size="lg" bold theme="inherit">
          {loginError}
        </Text>
      </Card>
    );
  }, [loginError]);

  if (loginCooldown) {
    return (
      <Cooldown
        setLoginCooldown={(i) => {
          onCooldownOff && onCooldownOff();
          setLoginCooldown(i);
        }}
        backToText={tx("actions.back_to_login").toUpperCase()}
      />
    );
  }

  if (showOtp) {
    return (
      <OtpForm
        onChange={(otp) =>
          setForm((previousValues) => {
            return { ...previousValues, otp };
          })
        }
        ErrorCard={ErrorCard}
        handleSubmit={handleSubmit}
        loading={loading}
        onClickBackToLogin={() => setShowOtp(false)}
      />
    );
  }

  return (
    <Form onSubmit={handleSubmit}>
      <FormRow>{ErrorCard}</FormRow>
      <EmailPasswordFormFragment
        email={newEmail ?? email}
        form={form}
        setForm={setForm}
      />
      <FormRow margin="sm">
        <Button
          id="login-button"
          data-testid="login-submit-button"
          type="submit"
          theme="primary"
          loading={loading}
          fullscreen={true}
          casing="capitalize"
        >
          {submitText}
        </Button>
      </FormRow>
      {includeOAuth && (
        <OAuthFormFragment submitText={submitText} sessionType={loginType} />
      )}
    </Form>
  );
};
